'use client'

import { Icon } from '@/components/ui/icon'
import { useCurrentUser } from '@/hooks/use-current-user'
import { usePendingAppointments } from '@/hooks/use-pending-appointments'
import { useFeatureFlags } from '@/lib/features/feature-flags-context'
import { useAppDispatch } from '@/lib/store'
import { setDrawerOpen } from '@/lib/store/features/booking/bookingSlice'
import { selectBookingProgress, selectIsDrawerOpen, selectSelectedServices } from '@/lib/store/features/booking/selectors'
import { cn } from '@heroui/react'
import { UserRole } from '@prisma/client'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

interface ReservationFABProps {
  variant: 'nav' | 'floating'
}

/**
 * Solid status dot on the reservation FAB.
 * Green = reservation is complete and ready to submit; red = it still needs
 * attention (incomplete steps, or a pending appointment awaiting payment).
 */
function StatusDot({ complete }: { complete: boolean }) {
  return (
    <span
      aria-hidden='true'
      className={cn(
        'absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-[var(--default)]',
        complete ? 'bg-success' : 'bg-danger'
      )}
    />
  )
}

export function ReservationFAB({ variant }: ReservationFABProps) {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const pathname = usePathname()
  const user = useCurrentUser()
  const selectedServiceIds = useSelector(selectSelectedServices)
  const progress = useSelector(selectBookingProgress)
  const isDrawerOpen = useSelector(selectIsDrawerOpen)
  const { hasPendingAppointments } = usePendingAppointments()
  const { bookingEnabled } = useFeatureFlags()

  const hasItems = selectedServiceIds.length > 0 || hasPendingAppointments

  // "In the booking flow" = the reservation drawer is open, or the user is on
  // the /book wizard or a /pay payment page. The FAB is the entry point into
  // that flow, so while the user is already inside it the button is muted,
  // non-interactive, and drops its status dot: there's nothing to open.
  const onBookingOrPaymentRoute =
    (pathname?.startsWith('/book') || pathname?.startsWith('/pay')) ?? false
  const inBookingFlow = isDrawerOpen || onBookingOrPaymentRoute

  // Ready to submit when every step is valid and there's no pending appointment
  // still awaiting payment.
  const isComplete = progress.percentage >= 100 && !hasPendingAppointments

  const handlePress = useCallback(() => {
    if (inBookingFlow) return
    if (hasItems) {
      dispatch(setDrawerOpen(true))
      return
    }
    router.push('/services')
  }, [dispatch, router, hasItems, inBookingFlow])

  // Floating variant: only show when no other nav has the booking icon.
  // The bottom nav (mobile, USER role) and the desktop header already
  // render their own booking triggers; the FAB is only needed on mobile
  // for ADMIN/INSTALLER whose bottom nav has no booking icon.
  // Booking off: nothing to open. The nav variant's slot is filled by BottomNav.
  if (!bookingEnabled) return null

  if (variant === 'floating') {
    if (!hasItems) return null
    if (user?.role === UserRole.USER) return null
  }

  // Active fill: the single accent (teal) with a white glyph. The teal
  // pairing rule (spec 3.1) makes teal a fill that carries white text, so
  // the FOREGROUND is text-accent-foreground, which now resolves to white.
  // Hover uses the semantic accent-hover token so it DARKENS. The old
  // --cw-lime-hover is lighter than the button-ramp fill bg-accent now
  // resolves to, which reversed the darken-on-interaction rule.
  const activeClasses = 'bg-accent text-accent-foreground hover:bg-accent-hover active:scale-95'

  const button = (
    <button
      type='button'
      onClick={handlePress}
      disabled={inBookingFlow}
      aria-label={hasItems ? 'View reservation' : 'Book an appointment'}
      className={cn(
        'relative flex items-center justify-center',
        'w-[44px] h-[44px] rounded-full transition-transform',
        inBookingFlow ? 'bg-muted/20 text-muted cursor-not-allowed' : activeClasses
      )}
    >
      <Icon icon='book-calendar' className='w-5 h-5' />
      {hasItems && !inBookingFlow && <StatusDot complete={isComplete} />}
    </button>
  )

  if (variant === 'nav') {
    return button
  }

  // Floating variant: only visible on mobile (desktop header has its own
  // trigger). The theme scope lives on <html>, so this container needs no
  // data-theme of its own.
  return (
    <div
      className={cn(
        'fixed z-[1001] md:hidden',
        'bottom-[calc(72px+0.75rem+env(safe-area-inset-bottom,0px))] right-4'
      )}
    >
      {button}
    </div>
  )
}
